import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { TPurchasingCodeDto } from '../../../core/interfaces/tpurchasing-code.model';
import { PurchasingCodeService } from '../../../core/services/adminService/purchasing-code.service';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { UserApiService } from '../../../core/services/user-api.service';
import { Subscription } from 'rxjs';
import { constantTxt } from '../../../core/constants/constants';

@Component({
  selector: 'app-purchasing-code',
  templateUrl: './purchasing-code.component.html',
  styleUrls: ['./purchasing-code.component.less'],
  imports: [
    RouterModule,
    CommonModule,
    NzButtonModule,
    NzInputModule,
    NzTableModule,
    NzSelectModule,
    ReactiveFormsModule,
  ],
})
export class PurchasingCodeComponent implements OnInit, OnDestroy {
  purchasingCodes: TPurchasingCodeDto[] = [];
  addForm!: FormGroup;
  showAddFormPopup = false;
  isEditMode: boolean = false;
  selectedIds: Set<number> = new Set();
  editingId: number | null = null;
  customNoDataMessage = constantTxt.noDataFound;

  getOfficesListSubscription!: Subscription;
  updatePOSubscription!: Subscription;
  addPOSubscription!: Subscription;
  pageSize = 50;

  constructor(
    private readonly fb: FormBuilder,
    private readonly purchasingCodeService: PurchasingCodeService,
    private readonly userApiService: UserApiService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadPurchasingCodes();
  }

  initializeForm() {
    this.addForm = this.fb.group({
      purchasingName: ['', Validators.required],
      purchasingStatus: ['true', Validators.required],
    });
  }

  loadPurchasingCodes() {
    this.getOfficesListSubscription = this.userApiService
      .getOfficesList()
      .subscribe({
        next: (res) => {
          let data = res.body;
          if (data) {
            this.purchasingCodes = data;
          }
        },
      });
  }

  addPurchasingCode() {
    if (this.addForm.valid) {
      if (this.isEditMode) {
        const formValue = this.addForm.value;
        const updatedCode: TPurchasingCodeDto = {
          purchasingId: this.editingId || 0,
          purchasingName: formValue.purchasingName,
          purchasingStatus:
            formValue.purchasingStatus === true ||
            formValue.purchasingStatus === 'true',
        };
        this.updatePOSubscription = this.purchasingCodeService
          .update(updatedCode)
          .subscribe(() => {
            this.loadPurchasingCodes();
            this.closePopup();
          });
      } else {
        if (confirm('Are you sure the user information is correct?')) {
          const formValue = this.addForm.value;
          const payload: TPurchasingCodeDto = {
            purchasingId: 0,
            purchasingName: formValue.purchasingName,
            purchasingStatus:
              formValue.purchasingStatus === true ||
              formValue.purchasingStatus === 'true',
          };
          this.addPOSubscription = this.purchasingCodeService
            .add(payload)
            .subscribe({
              next: (res) => {
                if (res.ok) {
                  this.loadPurchasingCodes();
                  this.closePopup();
                }
              },
              error: () => {
                console.log('Error adding purchasing code');
              },
            });
        }
      }
    } else {
      this.addForm.markAllAsTouched();
      alert('Please fill all mandatory fields.');
    }
  }

  startEdit(code: TPurchasingCodeDto) {
    this.showAddFormPopup = true;
    this.isEditMode = true;
    this.editingId = code.purchasingId;
    this.addForm.setValue({
      purchasingName: code.purchasingName,
      purchasingStatus: code.purchasingStatus ? 'true' : 'false',
    });
  }

  showAddForm() {
    this.showAddFormPopup = true;
  }

  closePopup() {
    this.showAddFormPopup = false;
    this.isEditMode = false;
    this.addForm.reset({ purchasingStatus: true });
  }

  deleteSelected() {
    if (confirm('Are you sure to delete checked item?')) {
      const ids = Array.from(this.selectedIds);
      ids.forEach((id) => {
        this.purchasingCodeService.delete(id).subscribe(() => {
          this.loadPurchasingCodes();
          this.selectedIds.delete(id);
        });
      });
    }
  }

  toggleSelection(id: number) {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  // Getters for template type safety
  get purchasingNameControl(): FormControl {
    return this.addForm.get('purchasingName') as FormControl;
  }
  get purchasingStatusControl(): FormControl {
    return this.addForm.get('purchasingStatus') as FormControl;
  }

  ngOnDestroy(): void {
    if (this.getOfficesListSubscription) {
      this.getOfficesListSubscription.unsubscribe();
    }

    if (this.updatePOSubscription) {
      this.updatePOSubscription.unsubscribe();
    }

    if (this.addPOSubscription) {
      this.addPOSubscription.unsubscribe();
    }
  }
}
